import { useState } from "react";
import { http, apiError } from "@/lib/api";
import { BellRinging, X, Lightning } from "@phosphor-icons/react";

const PRESETS = [
  { title: "Habit check-in", body: "Two minutes — log your habits so we can see how yesterday landed.", url: "/app" },
  { title: "Weigh-in reminder", body: "Sunday weigh-in time — hop on the scale and log it.", url: "/app/progress" },
  { title: "Workout nudge", body: "Session waiting on you today. Even 20 minutes counts.", url: "/app/program" },
  { title: "Meal plan drop", body: "New meal plan just landed. Tap to view.", url: "/app/meals" },
];

/**
 * NudgeClientButton — coach-side ad-hoc push notification composer.
 * Opens a small modal with preset nudges + custom fields.
 */
export default function NudgeClientButton({ clientId, clientName }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [url, setUrl] = useState("/app");
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState("");
  const [err, setErr] = useState("");

  const applyPreset = (p) => {
    setTitle(p.title);
    setBody(p.body);
    setUrl(p.url || "/app");
  };

  const send = async () => {
    setErr(""); setStatus(""); setSending(true);
    try {
      const { data } = await http.post(`/coach/push/nudge/${clientId}`, {
        title: title.trim(),
        body: body.trim(),
        url: url || "/app",
      });
      setStatus(`Sent to ${data.sent} device${data.sent === 1 ? "" : "s"}. 🔔`);
      setTimeout(() => { setOpen(false); setStatus(""); setTitle(""); setBody(""); setUrl("/app"); }, 1400);
    } catch (e) {
      setErr(apiError(e, "Nudge failed. Client may not have enabled notifications yet."));
    } finally { setSending(false); }
  };

  const closeModal = () => { if (!sending) { setOpen(false); setErr(""); setStatus(""); } };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="id-btn-secondary text-xs ml-2"
        data-testid="nudge-client-btn"
      >
        <BellRinging size={14} weight="bold" /> Nudge
      </button>

      {open && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={closeModal}
          data-testid="nudge-modal"
        >
          <div
            className="bg-[#0F0F0F] border border-[#FF3333]/60 max-w-lg w-full max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between p-4 border-b border-[#262626]">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 border border-[#FF3333]/60 bg-[#FF3333]/10 flex items-center justify-center shrink-0">
                  <BellRinging size={18} weight="fill" className="text-[#FF3333]" />
                </div>
                <div>
                  <div className="id-label text-[#FF3333] mb-0.5">— Push Nudge —</div>
                  <div className="font-oswald text-lg text-white uppercase tracking-wide leading-tight">
                    Ping {clientName?.split(" ")[0] || "client"}
                  </div>
                </div>
              </div>
              <button onClick={closeModal} className="text-zinc-500 hover:text-white" data-testid="nudge-close">
                <X size={20} weight="bold" />
              </button>
            </div>

            <div className="p-4 overflow-y-auto space-y-4">
              <div>
                <div className="id-label mb-2">Quick presets</div>
                <div className="grid grid-cols-1 gap-1.5">
                  {PRESETS.map((p, i) => (
                    <button
                      key={i}
                      onClick={() => applyPreset(p)}
                      className="text-left border border-[#262626] hover:border-[#FF3333]/40 px-3 py-2 text-xs font-manrope transition-colors"
                      data-testid={`nudge-preset-${i}`}
                    >
                      <div className="text-white font-semibold">{p.title}</div>
                      <div className="text-zinc-500 truncate">{p.body}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="id-label block mb-1">Title</label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={80}
                  className="id-input"
                  placeholder="e.g. Habit check-in"
                  data-testid="nudge-title"
                />
              </div>
              <div>
                <label className="id-label block mb-1">Body</label>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  maxLength={200}
                  rows={2}
                  className="id-input"
                  placeholder="Message the client sees on their lock screen"
                  data-testid="nudge-body"
                />
                <div className="text-[10px] text-zinc-600 font-manrope uppercase tracking-[0.15em] text-right mt-1">
                  {body.length}/200
                </div>
              </div>
              <div>
                <label className="id-label block mb-1">Tap opens</label>
                <select value={url} onChange={(e) => setUrl(e.target.value)} className="id-input text-xs uppercase tracking-[0.1em]" data-testid="nudge-url">
                  <option value="/app">Today dashboard</option>
                  <option value="/app/meals">Meals</option>
                  <option value="/app/program">Program</option>
                  <option value="/app/progress">Weight Log</option>
                  <option value="/app/stats">Stats</option>
                </select>
              </div>

              {err && (
                <div className="border border-[#FF3333]/60 bg-[#FF3333]/10 text-[#FF9999] px-3 py-2 text-xs font-manrope" data-testid="nudge-err">
                  {err}
                </div>
              )}
              {status && (
                <div className="border border-[#FF3333]/40 bg-[#FF3333]/10 text-[#FF9999] px-3 py-2 text-xs font-manrope flex items-center gap-2" data-testid="nudge-status">
                  <Lightning size={12} weight="fill" /> {status}
                </div>
              )}
            </div>

            <div className="p-4 border-t border-[#262626] flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
              <button
                onClick={closeModal}
                disabled={sending}
                className="id-btn-ghost text-xs disabled:opacity-40"
                data-testid="nudge-cancel"
              >
                Cancel
              </button>
              <button
                onClick={send}
                disabled={sending || !title.trim() || !body.trim()}
                className="id-btn-primary text-xs disabled:opacity-40"
                data-testid="nudge-send"
              >
                <BellRinging size={14} weight="fill" /> {sending ? "Sending…" : "Send push"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
